import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import ProducerForm from "../../components/ui/producerForm/producerForm.component";
import { useCRUD } from "../../api/crud";
import { EQUIPMENT_PRODUCERS_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";

export const ProducerDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, remove } = useCRUD(EQUIPMENT_PRODUCERS_ENDPOINT);
  const [deleteOpened, setDeleteOpened] = useState<boolean>(false);
  const [editOpened, setEditOpened] = useState<boolean>(false);
  const canDelete = useHasPermission("settlement.delete_equipmentproducer");
  const canChange = useHasPermission("settlement.change_equipmentproducer");

  const handleDelete = async () => {
    if (loading || !id) return;

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t("Producer deleted successfully"), SUCCESS_SNACKBAR);
      setDeleteOpened(false);
      navigate("/equipment-section/producers");
    } else {
      const msg =
        typeof result?.errorData === "string"
          ? result.errorData
          : (result?.errorData as { detail?: string; message?: string })?.detail ??
            (result?.errorData as { detail?: string; message?: string })?.message ??
            t("Failed to delete producer");
      enqueueSnackbar(msg, ERROR_SNACKBAR);
    }
  };

  const handleEditSuccess = () => {
    setEditOpened(false);
    window.dispatchEvent(new Event("equipmentProducerUpdated"));
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Edit producer"),
      onClick: () => setEditOpened(true),
      disabled: !canChange,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete producer"),
      onClick: () => {
        setDeleteOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDelete,
    },
  ];

  const deleteModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Deleting this producer will remove it and unassign its models. This action cannot be undone."
        )}
      </Typography>
      <Alert severity="warning">
        <Typography variant="body2">
          {t("This action cannot be undone. All associated data will be permanently deleted.")}
        </Typography>
      </Alert>
      <Button
        variant="contained"
        color="error"
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? t("Processing...") : t("Confirm deletion")}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editOpened}
        setOpened={setEditOpened}
        title={t("Edit producer")}
        content={id ? <ProducerForm id={id} onSuccess={handleEditSuccess} /> : <></>}
      />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t("Delete producer")}
        content={deleteModalContent}
      />
    </>
  );
};
